import type { GameEvent } from '../schema';

export const ROMANCE_EVENTS: GameEvent[] = [
  {
    id: 'romance.meeting',
    title: 'Someone interesting',
    description: 'A conversation that went on longer than it needed to. You are both aware of it.',
    category: 'romance',
    pools: ['pool.romance.early', 'pool.social'],
    weight: 9,
    cooldown: 2,
    requires: [{ k: 'marital', anyOf: ['single', 'divorced', 'widowed', 'separated'] }],
    weightMods: [
      { conditions: [{ k: 'trait', trait: 'socialConfidence', op: '>=', value: 58 }], multiplier: 1.5 },
      { conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 60 }], multiplier: 1.4 },
      { conditions: [{ k: 'num', path: 'lifeLoad', op: '>=', value: 72 }], multiplier: 0.5 },
      { conditions: [{ k: 'age', min: 48 }], multiplier: 0.65 },
    ],
    choices: [
      {
        id: 'pursue',
        label: 'Ask them out',
        hint: 'mixed',
        tags: ['romance', 'courage'],
        outcomes: [
          {
            weight: 62,
            label: 'They said yes',
            narrative: 'Three dates in and you are rearranging your week around them.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'trait', trait: 'socialConfidence', op: '>=', value: 60 }], multiplier: 1.5 },
              { conditions: [{ k: 'num', path: 'happiness', op: '>=', value: 60 }], multiplier: 1.25 },
            ],
            effects: [
              { k: 'person', op: 'add', kind: 'partner', tags: ['romance'] },
              { k: 'marital', status: 'dating' },
              { k: 'stat', stat: 'happiness', delta: 12 },
              { k: 'stat', stat: 'energy', delta: 5 },
              { k: 'memory', title: 'Met someone', tone: 'positive', category: 'relationship', weight: 5 },
              { k: 'triggerEvent', eventId: 'romance.compatibilityTest', afterYears: 2 },
            ],
          },
          {
            weight: 38,
            label: 'It did not go anywhere',
            narrative: 'Warm, mutual, and unreciprocated in the way that actually matters.',
            tone: 'neutral',
            effects: [
              { k: 'stat', stat: 'happiness', delta: -4 },
              { k: 'trait', trait: 'socialConfidence', delta: -2 },
              { k: 'skill', skill: 'social', delta: 2 },
            ],
          },
        ],
      },
      {
        id: 'hold',
        label: 'Leave it',
        detail: 'You have a lot on.',
        hint: 'safe',
        tags: ['avoidance'],
        outcomes: [
          {
            weight: 1,
            label: 'Nothing happened',
            narrative: 'You thought about messaging them for two weeks and then stopped thinking about it.',
            tone: 'neutral',
            effects: [
              { k: 'counter', name: 'missedConnections', delta: 1 },
              { k: 'inertia', op: 'pressure', delta: 4 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.compatibilityTest',
    title: 'The differences are real',
    description:
      'Two years in, the gaps are visible: money, family plans, how much of life should be scheduled.',
    category: 'romance',
    pools: ['pool.romance.commit', 'pool.romance.early', 'pool.family'],
    weight: 8,
    requires: [{ k: 'marital', anyOf: ['dating', 'partnered'] }],
    choices: [
      {
        id: 'talk',
        label: 'Have the difficult conversation',
        hint: 'mixed',
        tags: ['relationship', 'courage'],
        outcomes: [
          {
            weight: 58,
            label: 'You found the overlap',
            narrative: 'Four hours, one bottle of wine, and an actual agreement about the next ten years.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 50 }], multiplier: 1.6 },
              { conditions: [{ k: 'trait', trait: 'conflictAvoidance', op: '<=', value: 40 }], multiplier: 1.5 },
            ],
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: 16 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 12 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: -10 },
              { k: 'stat', stat: 'happiness', delta: 8 },
              { k: 'flag', flag: 'partnerAligned', value: true },
            ],
          },
          {
            weight: 42,
            label: 'It surfaced something worse',
            narrative: 'The conversation revealed you want genuinely different lives. Neither of you is wrong.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: 22 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: -12 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'triggerEvent', eventId: 'romance.breakup', afterYears: 1 },
            ],
          },
        ],
      },
      {
        id: 'avoid',
        label: 'Avoid it',
        detail: 'Things are good. Why ruin it.',
        hint: 'risky',
        tags: ['avoidance'],
        defers: { id: 'int.relationshipTalk', label: 'Have the conversation with your partner', topic: 'relationship', urgency: 34, resumeEventId: 'romance.overdueConversation', decayEventId: 'romance.driftedApart' },
        outcomes: [
          {
            weight: 1,
            label: 'Left unsaid',
            narrative: 'The topic acquired a careful shape you both learned to walk around.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: -6 },
              { k: 'habit', habit: 'relationshipCare', delta: -6 },
              { k: 'inertia', op: 'pressure', delta: 6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.overdueConversation',
    title: 'It has been years',
    description: 'The thing you did not say is still not said, and it has grown.',
    category: 'romance',
    pools: ['pool.relationship.strain', 'pool.inertia', 'pool.family', 'pool.romance.commit'],
    weight: 6,
    requires: [{ k: 'intention', topic: 'relationship', present: true }, { k: 'marital', anyOf: ['dating', 'partnered', 'married'] }],
    choices: [
      {
        id: 'now',
        label: 'Say it now',
        hint: 'mixed',
        tags: ['courage', 'relationship'],
        outcomes: [
          {
            weight: 52,
            label: 'Relief',
            narrative: 'They had been waiting for you to bring it up. Both of you had.',
            tone: 'positive',
            effects: [
              { k: 'intention', op: 'resolve', id: 'int.relationshipTalk' },
              { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: 14 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: -14 },
              { k: 'stat', stat: 'stress', delta: -10 },
              { k: 'habit', habit: 'relationshipCare', delta: 8 },
            ],
          },
          {
            weight: 48,
            label: 'Too late to be easy',
            narrative: 'Years of silence had made the topic heavier than it needed to be.',
            tone: 'negative',
            effects: [
              { k: 'intention', op: 'resolve', id: 'int.relationshipTalk' },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: 16 },
              { k: 'stat', stat: 'stress', delta: 8 },
            ],
          },
        ],
      },
      {
        id: 'still-no',
        label: 'Still not now',
        hint: 'risky',
        outcomes: [
          {
            weight: 1,
            label: 'Avoided again',
            narrative: 'Another year of the careful shape.',
            tone: 'negative',
            effects: [
              { k: 'intention', op: 'defer', id: 'int.relationshipTalk' },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: -8 },
              { k: 'inertia', op: 'pressure', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.driftedApart',
    title: 'You live parallel lives',
    description: 'Nothing happened. That is precisely what happened, repeatedly, for years.',
    category: 'romance',
    pools: ['pool.inertia'],
    weight: 0,
    automatic: true,
    choices: [
      {
        id: 'ack',
        label: 'Notice the distance',
        outcomes: [
          {
            weight: 1,
            label: 'Drifted apart',
            narrative: 'Courteous, coordinated, and no longer close.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: -24 },
              { k: 'relationship', who: { r: 'spouse' }, dim: 'closeness', delta: -24 },
              { k: 'family', op: 'adjustHarmony', delta: -14 },
              { k: 'regret', op: 'add', id: 'regret.drift', label: 'Let a relationship drift', weight: 20 },
              { k: 'stat', stat: 'happiness', delta: -8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.proposal',
    title: 'Make it permanent?',
    description:
      'It works. Marriage would change the legal, financial and emotional architecture of both your lives.',
    category: 'romance',
    pools: ['pool.romance.commit', 'pool.romance.early', 'pool.family', 'pool.social'],
    weight: 9,
    cooldown: 7,
    requires: [
      { k: 'marital', anyOf: ['dating', 'partnered'] },
      { k: 'hasPerson', kind: 'partner' },
    ],
    weightMods: [
      { conditions: [{ k: 'flag', flag: 'partnerAligned' }], multiplier: 2.2 },
      { conditions: [{ k: 'age', min: 28, max: 40 }], multiplier: 1.5 },
    ],
    choices: [
      {
        id: 'marry',
        label: 'Get married',
        hint: 'mixed',
        tags: ['relationship', 'commitment'],
        cost: [{ k: 'cashIncomeMonths', months: -2, note: 'wedding' }],
        outcomes: [
          {
            weight: 1,
            label: 'Married',
            narrative: 'A room full of people who know you, and a legal instrument neither of you read closely.',
            tone: 'positive',
            effects: [
              { k: 'person', op: 'convert', who: { r: 'partner' }, to: 'spouse' },
              { k: 'marital', status: 'married' },
              { k: 'relationship', who: { r: 'spouse' }, dim: 'trust', delta: 12 },
              { k: 'relationship', who: { r: 'spouse' }, dim: 'support', delta: 16 },
              { k: 'stat', stat: 'happiness', delta: 16 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'fixedExpenses', mode: 'mult', value: 1.32 },
              { k: 'family', op: 'adjustHarmony', delta: 12 },
              { k: 'aspiration', op: 'progress', defId: 'dream.partnership', delta: 45 },
              { k: 'memory', title: 'Got married', tone: 'milestone', category: 'relationship', weight: 10 },
            ],
          },
        ],
      },
      {
        id: 'commit',
        label: 'Commit without marrying',
        detail: 'Move in. Share the life, skip the paperwork.',
        hint: 'safe',
        requires: [{ k: 'marital', anyOf: ['dating'] }],
        tags: ['relationship'],
        outcomes: [
          {
            weight: 1,
            label: 'Moved in together',
            narrative: 'Same life, fewer forms. Your relatives will ask about it annually.',
            tone: 'positive',
            effects: [
              { k: 'marital', status: 'partnered' },
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 12 },
              { k: 'stat', stat: 'happiness', delta: 11 },
              { k: 'fixedExpenses', mode: 'mult', value: 1.22 },
              { k: 'aspiration', op: 'progress', defId: 'dream.partnership', delta: 35 },
            ],
          },
        ],
      },
      {
        id: 'notYet',
        label: 'Not yet',
        hint: 'mixed',
        outcomes: [
          {
            weight: 1,
            label: 'Waited',
            narrative: 'Reasonable. They heard it as an answer rather than a delay.',
            tone: 'neutral',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: -8 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: 8 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.breakup',
    title: 'This is not working',
    description: 'One of you says it. It has been true for a while.',
    category: 'romance',
    pools: ['pool.relationship.strain'],
    weight: 5,
    requires: [{ k: 'marital', anyOf: ['dating', 'partnered'] }],
    weightMods: [{ conditions: [{ k: 'num', path: 'householdHarmony', op: '<', value: 40 }], multiplier: 2.4 }],
    choices: [
      {
        id: 'end',
        label: 'End it',
        hint: 'costly',
        tags: ['courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Broke up',
            narrative: 'Two months of grief and then, unexpectedly, a great deal of energy.',
            tone: 'neutral',
            effects: [
              { k: 'person', op: 'convert', who: { r: 'partner' }, to: 'ex' },
              { k: 'marital', status: 'single' },
              { k: 'fixedExpenses', mode: 'mult', value: 0.82 },
              { k: 'stat', stat: 'happiness', delta: -14 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'schedule', afterYears: 2, label: 'Recovered from the breakup', effects: [{ k: 'stat', stat: 'happiness', delta: 12 }, { k: 'stat', stat: 'energy', delta: 8 }, { k: 'trait', trait: 'resilience', delta: 4 }] },
            ],
          },
        ],
      },
      {
        id: 'try',
        label: 'Try to fix it',
        hint: 'mixed',
        tags: ['relationship'],
        outcomes: [
          {
            weight: 42,
            label: 'You rebuilt it',
            narrative: 'Counselling, effort, and a genuinely better relationship on the other side.',
            tone: 'positive',
            favouredBy: [
              { conditions: [{ k: 'num', path: 'supportNetwork', op: '>=', value: 55 }], multiplier: 1.5 },
              { conditions: [{ k: 'skill', skill: 'communication', op: '>=', value: 55 }], multiplier: 1.6 },
            ],
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'trust', delta: 18 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: -18 },
              { k: 'family', op: 'adjustHarmony', delta: 14 },
              { k: 'cashIncomeMonths', months: -0.6, note: 'counselling' },
              { k: 'stat', stat: 'happiness', delta: 8 },
            ],
          },
          {
            weight: 58,
            label: 'It ended anyway',
            narrative: 'A slower, more exhausting version of the same outcome.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'convert', who: { r: 'partner' }, to: 'ex' },
              { k: 'marital', status: 'single' },
              { k: 'stat', stat: 'happiness', delta: -16 },
              { k: 'stat', stat: 'energy', delta: -10 },
              { k: 'stat', stat: 'stress', delta: 12 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.marriageStrain',
    title: 'Something has gone quiet',
    description:
      'You are efficient co-managers of a household. The other part has not been attended to in some time.',
    category: 'romance',
    pools: ['pool.relationship.strain'],
    weight: 7,
    cooldown: 4,
    requires: [{ k: 'marital', anyOf: ['married', 'partnered'] }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'lifeLoad', op: '>=', value: 68 }], multiplier: 1.9 },
      { conditions: [{ k: 'habit', habit: 'relationshipCare', op: '<=', value: 35 }], multiplier: 1.8 },
      { conditions: [{ k: 'num', path: 'workload', op: '>=', value: 72 }], multiplier: 1.5 },
    ],
    choices: [
      {
        id: 'invest',
        label: 'Put real time into it',
        detail: 'Protect two evenings a week. Say no to work.',
        hint: 'costly',
        tags: ['relationship', 'balance'],
        outcomes: [
          {
            weight: 1,
            label: 'Reconnected',
            narrative: 'It took months of unremarkable evenings, which is how it always works.',
            tone: 'positive',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: 18 },
              { k: 'relationship', who: { r: 'partner' }, dim: 'conflict', delta: -12 },
              { k: 'habit', habit: 'relationshipCare', delta: 14 },
              { k: 'family', op: 'adjustHarmony', delta: 12 },
              { k: 'career', op: 'adjust', field: 'performance', delta: -4 },
              { k: 'stat', stat: 'happiness', delta: 10 },
            ],
          },
        ],
      },
      {
        id: 'later',
        label: 'After this quarter',
        hint: 'risky',
        tags: ['avoidance'],
        defers: { id: 'int.relationshipTime', label: 'Make time for your partner', topic: 'relationship', urgency: 30, decayEventId: 'romance.driftedApart' },
        outcomes: [
          {
            weight: 1,
            label: 'Postponed',
            narrative: 'There is always a quarter.',
            tone: 'negative',
            effects: [
              { k: 'relationship', who: { r: 'partner' }, dim: 'closeness', delta: -10 },
              { k: 'habit', habit: 'relationshipCare', delta: -6 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.divorce',
    title: 'It is over',
    description: 'Years of managed distance have arrived somewhere. Separating will cost money and identity.',
    category: 'romance',
    pools: ['pool.relationship.strain', 'pool.crisis'],
    weight: 4,
    requires: [{ k: 'marital', anyOf: ['married'] }],
    weightMods: [
      { conditions: [{ k: 'num', path: 'householdHarmony', op: '<', value: 32 }], multiplier: 3 },
      { conditions: [{ k: 'num', path: 'burnoutLoad', op: '>=', value: 60 }], multiplier: 1.4 },
    ],
    choices: [
      {
        id: 'divorce',
        label: 'Separate',
        hint: 'costly',
        tags: ['courage'],
        outcomes: [
          {
            weight: 1,
            label: 'Divorced',
            narrative: 'Solicitors, a smaller flat, and the strange lightness of no longer pretending.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'convert', who: { r: 'spouse' }, to: 'ex' },
              { k: 'marital', status: 'divorced' },
              { k: 'cashIncomeMonths', months: -8, note: 'divorce settlement' },
              { k: 'invest', op: 'shock', assetId: 'balanced', fraction: -0.4 },
              { k: 'stat', stat: 'happiness', delta: -20 },
              { k: 'stat', stat: 'stress', delta: 18 },
              { k: 'relationshipAll', kind: 'child', dim: 'conflict', delta: 12 },
              { k: 'counter', name: 'crises', delta: 1 },
              { k: 'memory', title: 'Divorced', tone: 'negative', category: 'relationship', weight: 10 },
              { k: 'schedule', afterYears: 3, label: 'Life after the divorce', effects: [{ k: 'stat', stat: 'happiness', delta: 16 }, { k: 'trait', trait: 'resilience', delta: 7 }, { k: 'stat', stat: 'purpose', delta: 6 }] },
            ],
          },
        ],
      },
      {
        id: 'stay',
        label: 'Stay, for the children',
        hint: 'risky',
        requires: [{ k: 'hasPerson', kind: 'child' }],
        showWhenLocked: true,
        tags: ['family', 'sacrifice'],
        outcomes: [
          {
            weight: 1,
            label: 'Stayed',
            narrative: 'A functional household with the temperature turned down. The children noticed.',
            tone: 'negative',
            effects: [
              { k: 'marital', status: 'married' },
              { k: 'stat', stat: 'happiness', delta: -12 },
              { k: 'stat', stat: 'stress', delta: 10 },
              { k: 'family', op: 'adjustHarmony', delta: -8 },
              { k: 'relationshipAll', kind: 'child', dim: 'closeness', delta: 5 },
              { k: 'regret', op: 'add', id: 'regret.stayed', label: 'Stayed in a marriage that had ended', weight: 20 },
            ],
          },
        ],
      },
      {
        id: 'counselling',
        label: 'One serious attempt at repair',
        hint: 'mixed',
        tags: ['relationship'],
        outcomes: [
          {
            weight: 32,
            label: 'It came back',
            narrative: 'Against the odds and after a great deal of work, you liked each other again.',
            tone: 'positive',
            favouredBy: [{ conditions: [{ k: 'num', path: 'supportNetwork', op: '>=', value: 60 }], multiplier: 1.6 }],
            effects: [
              { k: 'relationship', who: { r: 'spouse' }, dim: 'trust', delta: 22 },
              { k: 'relationship', who: { r: 'spouse' }, dim: 'closeness', delta: 20 },
              { k: 'relationship', who: { r: 'spouse' }, dim: 'conflict', delta: -24 },
              { k: 'family', op: 'adjustHarmony', delta: 24 },
              { k: 'cashIncomeMonths', months: -1, note: 'counselling' },
              { k: 'stat', stat: 'happiness', delta: 14 },
              { k: 'aspiration', op: 'progress', defId: 'dream.partnership', delta: 30 },
            ],
          },
          {
            weight: 68,
            label: 'It confirmed the ending',
            narrative: 'The counselling gave you a shared vocabulary for why it was finished.',
            tone: 'negative',
            effects: [
              { k: 'person', op: 'convert', who: { r: 'spouse' }, to: 'ex' },
              { k: 'marital', status: 'divorced' },
              { k: 'cashIncomeMonths', months: -7, note: 'divorce' },
              { k: 'stat', stat: 'happiness', delta: -16 },
              { k: 'counter', name: 'crises', delta: 1 },
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'romance.secondChance',
    title: 'Later than you expected',
    description: 'You meet someone at an age when you had quietly stopped expecting to.',
    category: 'romance',
    pools: ['pool.romance.early', 'pool.retired.family'],
    weight: 5,
    cooldown: 6,
    requires: [
      { k: 'marital', anyOf: ['single', 'divorced', 'widowed'] },
      { k: 'age', min: 45 },
    ],
    weightMods: [{ conditions: [{ k: 'num', path: 'socialHealth', op: '>=', value: 55 }], multiplier: 1.6 }],
    choices: [
      {
        id: 'yes',
        label: 'Let it happen',
        hint: 'mixed',
        tags: ['romance'],
        outcomes: [
          {
            weight: 1,
            label: 'A late partnership',
            narrative: 'Fewer illusions, better conversation, and both of you aware of how much time is left.',
            tone: 'positive',
            effects: [
              { k: 'person', op: 'add', kind: 'partner', tags: ['late-romance'], bond: { compatibility: 70, trust: 60, closeness: 58, support: 60, conflict: 6 } },
              { k: 'marital', status: 'partnered' },
              { k: 'stat', stat: 'happiness', delta: 18 },
              { k: 'stat', stat: 'purpose', delta: 8 },
              { k: 'aspiration', op: 'progress', defId: 'dream.partnership', delta: 40 },
              { k: 'memory', title: 'Found someone, later', tone: 'milestone', category: 'relationship', weight: 8 },
            ],
          },
        ],
      },
      {
        id: 'no',
        label: 'Stay as you are',
        hint: 'safe',
        outcomes: [
          {
            weight: 1,
            label: 'Declined',
            narrative: 'You like your life the shape it is. That is a legitimate answer.',
            tone: 'neutral',
            effects: [{ k: 'stat', stat: 'happiness', delta: 2 }],
          },
        ],
      },
    ],
  },
];
